import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  verificationPhotoApi,
  type VerificationPhoto,
} from '../../services/verificationPhotoService';
import { describeCapture } from './VerificationPhotoThumbnails';

export interface VerificationPhotoLightboxProps {
  open: boolean;
  onClose: () => void;
  pharmacyId: string;
  orderId: string;
  photo: VerificationPhoto | null;
  orderNumber?: string | null;
}

/**
 * PRX-68 — the one full-size viewer every surface opens for a
 * product-verification photo (PRX-51). Fetches its own bytes through
 * `verificationPhotoApi.fetchImage` — there is no signed URL to embed, the
 * same reason `VerificationPhotoThumbnails` fetches its tiles — and revokes
 * the object URL on close or unmount so nothing outlives the dialog.
 */
export const VerificationPhotoLightbox: React.FC<VerificationPhotoLightboxProps> = ({
  open,
  onClose,
  pharmacyId,
  orderId,
  photo,
  orderNumber,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !photo) return undefined;

    let cancelled = false;
    setUrl(null);
    setFailed(false);

    const load = async () => {
      try {
        const blob = await verificationPhotoApi.fetchImage(pharmacyId, orderId, photo.id);
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        urlRef.current = objectUrl;
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pharmacyId, orderId, photo?.id]);

  if (!open || !photo) return null;

  const loading = !url && !failed;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle>
        {orderNumber ? `Product verification photo — order ${orderNumber}` : 'Product verification photo'}
      </DialogTitle>
      <DialogContent>
        {failed ? (
          <Alert severity="error">The photo could not be loaded.</Alert>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              component="img"
              src={url ?? undefined}
              alt={describeCapture(photo)}
              style={{ maxWidth: '100%', maxHeight: '75vh' }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {describeCapture(photo)}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerificationPhotoLightbox;
